import clsx from 'clsx';
import React, { type ReactNode } from 'react';

/**
 * Scrollable content region inside an Ant Design modal body.
 * Pair with ModalLayout + ModalLayout.Footer so actions stay pinned while content scrolls.
 */
function ModalLayoutContent({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={clsx(
        // Use flex-basis `auto` so the scroll container can correctly take up
        // the available height from the modal body (avoids "no scroll" collapse).
        'min-h-0 flex-[1_1_auto] overflow-x-hidden overflow-y-auto',
        // Slight padding so focus rings / shadows are not clipped at the scroll edges
        'pr-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

ModalLayoutContent.displayName = 'ModalLayoutContent';

/**
 * Sticky footer row (toolbar / actions) below scrollable modal content.
 * Renders only when children are passed.
 */
function ModalLayoutFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element | null {
  if (children == null || children === false) {
    return null;
  }
  return (
    <div className={clsx('border-gray-medium mt-4 shrink-0 border-t bg-white pt-4', className)}>
      {children}
    </div>
  );
}

ModalLayoutFooter.displayName = 'ModalLayoutFooter';

export interface ModalLayoutProps {
  children: ReactNode;
  /**
   * Max height for the whole composer content area (content scrolls, footer stays visible).
   * Default is tuned for the email composer.
   */
  maxHeight?: string;
  /** Extra classes on the root column. */
  className?: string;
}

/**
 * Column layout for content rendered inside `modal.info` / `Modal.confirm` bodies.
 * Ant Design owns the modal title; this splits body into scrollable content + optional fixed footer.
 *
 * This component also applies `maxHeight` by default so it behaves consistently without
 * relying on the modal container to cap height.
 */
function ModalLayout({
  children,
  maxHeight = 'calc(100vh - 140px)',
  className,
}: ModalLayoutProps): React.JSX.Element {
  return (
    <div
      className={clsx(
        // flex-basis `auto` so the internal scroll area gets real height.
        'flex min-h-0 w-full flex-[1_1_auto] flex-col overflow-hidden',
        className,
      )}
      style={{ maxHeight }}
    >
      {children}
    </div>
  );
}

ModalLayout.displayName = 'ModalLayout';
ModalLayout.Content = ModalLayoutContent;
ModalLayout.Footer = ModalLayoutFooter;

export default ModalLayout;
